// Bottom-sheet inline calendar.
// Два режими:
//   • single — для snooze у Reminders (тільки майбутнє)
//   • range  — для PeriodPicker (вибір "з .. по")
//
// API:
//   <DateSheet open onClose
//     mode="single" minDate=today onPick={(days)=>..} />
//   <DateSheet open onClose
//     mode="range" initialFrom initialTo onPickRange={(from,to)=>..} />

import { useEffect, useState } from "react";

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fromIso(iso: string): Date | null {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map((s) => Number(s));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

const WEEKDAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"];
const MONTH_LABELS = [
  "Січень",
  "Лютий",
  "Березень",
  "Квітень",
  "Травень",
  "Червень",
  "Липень",
  "Серпень",
  "Вересень",
  "Жовтень",
  "Листопад",
  "Грудень",
];

interface BaseProps {
  open: boolean;
  onClose: () => void;
  title?: string;
}

interface SingleProps extends BaseProps {
  mode?: "single";
  onPick?: (daysFromToday: number) => void;
  onPickDate?: (iso: string) => void;
  minDate?: Date;
}

interface RangeProps extends BaseProps {
  mode: "range";
  initialFrom?: string;
  initialTo?: string;
  onPickRange: (from: string, to: string) => void;
  /** Якщо треба обмежити майбутнім (default — будь-яка дата). */
  minDate?: Date;
  maxDate?: Date;
}

export function DateSheet(props: SingleProps | RangeProps) {
  const isRange = props.mode === "range";
  const today = startOfDay(new Date());
  const min = props.minDate
    ? startOfDay(props.minDate)
    : isRange
      ? null
      : today;
  const max = isRange && "maxDate" in props && props.maxDate
    ? startOfDay(props.maxDate)
    : null;

  // Стартовий місяць — місяць initial-from / минулого місяця / поточний
  const initialFrom = isRange
    ? fromIso((props as RangeProps).initialFrom ?? "")
    : null;
  const initialTo = isRange
    ? fromIso((props as RangeProps).initialTo ?? "")
    : null;

  const [viewMonth, setViewMonth] = useState<Date>(() => {
    const seed = initialFrom ?? today;
    return new Date(seed.getFullYear(), seed.getMonth(), 1);
  });
  const [selected, setSelected] = useState<Date | null>(null);
  const [rangeStart, setRangeStart] = useState<Date | null>(initialFrom);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(initialTo);

  // Скидаємо стан при відкритті (щоб не тягнули старі значення)
  useEffect(() => {
    if (!props.open) return;
    if (isRange) {
      setRangeStart(initialFrom);
      setRangeEnd(initialTo);
      const seed = initialFrom ?? today;
      setViewMonth(new Date(seed.getFullYear(), seed.getMonth(), 1));
    } else {
      setSelected(null);
      setViewMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  if (!props.open) return null;

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const offsetCells = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (Date | null)[] = [];
  for (let i = 0; i < offsetCells; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const isOutOfRange = (d: Date) => {
    if (min && d.getTime() < min.getTime()) return true;
    if (max && d.getTime() > max.getTime()) return true;
    return false;
  };

  function shiftMonth(delta: number) {
    setViewMonth(new Date(year, month + delta, 1));
  }

  function tap(d: Date) {
    if (isOutOfRange(d)) return;
    if (!isRange) {
      setSelected(d);
      return;
    }
    // range logic: 1й тап = start (clear end). 2й тап = end (якщо < start — свап).
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(d);
      setRangeEnd(null);
      return;
    }
    if (d.getTime() < rangeStart.getTime()) {
      setRangeEnd(rangeStart);
      setRangeStart(d);
    } else {
      setRangeEnd(d);
    }
  }

  function confirm() {
    if (isRange) {
      if (!rangeStart || !rangeEnd) return;
      (props as RangeProps).onPickRange(toIso(rangeStart), toIso(rangeEnd));
    } else {
      if (!selected) return;
      const sp = props as SingleProps;
      if (sp.onPickDate) sp.onPickDate(toIso(selected));
      if (sp.onPick) {
        const days = Math.round(
          (selected.getTime() - today.getTime()) / 86400000,
        );
        sp.onPick(days);
      }
    }
    props.onClose();
  }

  // Підпис кнопки + стан "готово?"
  let confirmLabel = "Виберіть день";
  let confirmReady = false;
  if (isRange) {
    if (rangeStart && rangeEnd) {
      confirmLabel = `${toIso(rangeStart)} → ${toIso(rangeEnd)}`;
      confirmReady = true;
    } else if (rangeStart) {
      confirmLabel = "Виберіть другу дату";
    } else {
      confirmLabel = "Виберіть першу дату";
    }
  } else {
    if (selected) {
      const sp = props as SingleProps;
      const days = Math.round(
        (selected.getTime() - today.getTime()) / 86400000,
      );
      confirmLabel = sp.onPick
        ? `Відкласти на ${days} дн.`
        : `Обрати ${toIso(selected)}`;
      confirmReady = true;
    }
  }

  return (
    <div className="nz-dim" onClick={props.onClose}>
      <div
        className="nz-sheet nz-sheet-cal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="nz-sheet-grip" />
        <div className="nz-sheet-title">
          {props.title ?? (isRange ? "📅 Виберіть діапазон" : "📅 Виберіть дату")}
        </div>

        <div className="nz-cal-head">
          <button
            type="button"
            className="nz-cal-nav"
            onClick={() => shiftMonth(-1)}
            aria-label="Попередній місяць"
          >
            ‹
          </button>
          <div className="nz-cal-month">
            {MONTH_LABELS[month]} {year}
          </div>
          <button
            type="button"
            className="nz-cal-nav"
            onClick={() => shiftMonth(1)}
            aria-label="Наступний місяць"
          >
            ›
          </button>
        </div>

        <div className="nz-cal-wd">
          {WEEKDAY_LABELS.map((w) => (
            <div key={w}>{w}</div>
          ))}
        </div>

        <div className="nz-cal-grid">
          {cells.map((d, i) => {
            if (!d) return <div key={i} className="nz-cal-cell empty" />;
            const out = isOutOfRange(d);
            const isToday = d.getTime() === today.getTime();
            let isSel = false;
            let inRange = false;
            let isStart = false;
            let isEnd = false;
            if (isRange) {
              if (rangeStart && d.getTime() === rangeStart.getTime()) {
                isSel = true;
                isStart = true;
              }
              if (rangeEnd && d.getTime() === rangeEnd.getTime()) {
                isSel = true;
                isEnd = true;
              }
              if (
                rangeStart &&
                rangeEnd &&
                d.getTime() > rangeStart.getTime() &&
                d.getTime() < rangeEnd.getTime()
              ) {
                inRange = true;
              }
            } else {
              isSel = selected?.getTime() === d.getTime();
            }
            return (
              <button
                key={i}
                type="button"
                className="nz-cal-cell"
                data-past={out}
                data-today={isToday}
                data-on={isSel}
                data-in-range={inRange}
                data-range-start={isStart}
                data-range-end={isEnd}
                disabled={out}
                onClick={() => tap(d)}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>

        <div className="nz-cal-actions">
          <button
            type="button"
            className="nz-sheet-cancel"
            onClick={props.onClose}
          >
            Скасувати
          </button>
          <button
            type="button"
            className="nz-btn primary nz-cal-confirm"
            disabled={!confirmReady}
            onClick={confirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
